import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
} from '@mui/material'
import ContainedButton, {
  ContainedButtonVariant,
} from '../Buttons/ContainedButton'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'
import DebouncedAutocomplete from '../Elements/DebouncedAutocomplete'

interface PotentialEventOwner {
  fullName: string
  roleName: string
  actorKey: number
}

interface ReassignEventOwnerModalProps {
  open: boolean
  onClose: () => void
  onReassign: () => void
  selectedCount: number
  selectedEventOwner: PotentialEventOwner | null
  setSelectedEventOwner: (owner: PotentialEventOwner | null) => void
  eventOwnerOptions: PotentialEventOwner[]
  isLoading: boolean
  isReassigning: boolean
  onInputChange: (newValue: string) => void
}

const ReassignEventOwnerModal: React.FC<ReassignEventOwnerModalProps> = ({
  open,
  onClose,
  onReassign,
  selectedCount,
  selectedEventOwner,
  setSelectedEventOwner,
  eventOwnerOptions,
  isReassigning,
  onInputChange,
}) => {
  const { t } = useTranslation()
  const showOnDesktop = useShowOnDesktop()

  return (
    <Dialog
      open={open}
      onClose={!isReassigning ? onClose : undefined}
      aria-labelledby="reassign-event-owner-dialog-title"
    >
      <DialogTitle id="reassign-event-owner-dialog-title">
        {t('Events.Reassign.ModalTitle', 'Reassign to New Event Owner')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          {t(
            'Events.Reassign.ModalDescription',
            `You are about to reassign {{count}} event(s) to a new owner.`,
            { count: selectedCount }
          )}
        </Typography>
        <Box mt={2}>
          <DebouncedAutocomplete
            id="event-owner-select"
            options={eventOwnerOptions}
            optionsLabel={(option) =>
              `${option.fullName} (${option.roleName} - ${option.actorKey})`
            }
            noOptionsTextValue={t('Events.Reassign.NoOptions', 'No Options')}
            renderOptions={(
              props: React.HTMLAttributes<HTMLLIElement>,
              option: PotentialEventOwner
            ) => (
              <Box component="li" {...props} key={option.actorKey}>
                {option.fullName} ({option.roleName})
              </Box>
            )}
            initialValue={selectedEventOwner}
            handleChange={(newValue: PotentialEventOwner | null) =>
              setSelectedEventOwner(newValue)
            }
            updateSearchResults={(search) => {
              onInputChange(search)
            }}
            textFieldLabelValue={t(
              'Events.Reassign.EventOwnerSelect',
              'Select New Event Owner'
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={isReassigning}>
          {t('Common.Cancel', 'Cancel')}
        </Button>
        <ContainedButton
          id="reassignEvents"
          variant={ContainedButtonVariant.ReassignEvents}
          disabled={!selectedEventOwner || isReassigning}
          onClick={onReassign}
          fullWidth={!showOnDesktop}
        />
      </DialogActions>
    </Dialog>
  )
}

export default ReassignEventOwnerModal
